import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Divider,
  Stack,
  InputAdornment,
  Badge,
  Avatar,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Security as SecurityIcon,
  Group as GroupIcon,
  VpnKey as PermissionIcon,
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Refresh as RefreshIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  AccountBalance as FinanceIcon,
  Receipt as ProcurementIcon,
  EventNote as LeaveIcon,
  Assessment as ReportsIcon,
  Settings as SettingsIcon,
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon,
} from "@mui/icons-material";

// Import your existing API service
import rolesAPI from "../../services/api/roles.api";
import permissionsAPI from "../../services/api/permissions.api";
import PermissionTreeView from "../../components/features/permissions/PermissionTreeView";

const RoleManagementPage = () => {
  // State management
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState(0);

  // Dialog states
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [selectedRoleForPermissions, setSelectedRoleForPermissions] =
    useState(null);

  // Form states
  const [roleForm, setRoleForm] = useState({
    role_name: "",
    description: "",
    is_active: true,
    is_manager: false,
    is_admin: false,
    is_board: false,
    hierarchy_level: null,
  });
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [availableSearchTerm, setAvailableSearchTerm] = useState("");
  const [selectedSearchTerm, setSelectedSearchTerm] = useState("");

  // Menu and notification states
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [rolesResponse, permissionsResponse] = await Promise.all([
        rolesAPI.getAllRoles(),
        rolesAPI.getAllPermissions(),
      ]);

      if (rolesResponse.success) {
        const rolesData = Array.isArray(rolesResponse.data)
          ? rolesResponse.data
          : [];
        setRoles(rolesData);
      } else {
        setRoles([]);
      }

      if (permissionsResponse.success) {
        // Handle different response structures
        let permissionsData = [];
        if (Array.isArray(permissionsResponse.data)) {
          permissionsData = permissionsResponse.data;
        } else if (
          permissionsResponse.data &&
          typeof permissionsResponse.data === "object"
        ) {
          // Backend returns { grouped: {...}, all: [...] } format
          permissionsData =
            permissionsResponse.data.all ||
            permissionsResponse.data.permissions ||
            permissionsResponse.data.data ||
            Object.values(permissionsResponse.data)
              .flat()
              .filter((item) => item && item.id);
        }
        console.log(
          "Permissions loaded:",
          permissionsData.length,
          "permissions"
        );
        console.log(
          "Edit Settings permission found:",
          permissionsData.find((p) => p.name === "Edit Settings")
        );
        setPermissions(Array.isArray(permissionsData) ? permissionsData : []);
      } else {
        setPermissions([]);
      }
    } catch (error) {
      console.error("Load data error:", error);
      showNotification("Error loading data: " + error.message, "error");
      setRoles([]);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, severity = "success") => {
    setNotification({ open: true, message, severity });
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  // Role CRUD operations
  const handleCreateRole = async () => {
    try {
      const response = await rolesAPI.createRole(roleForm);
      if (response.success) {
        showNotification("Role created successfully");
        setRoleDialogOpen(false);
        setRoleForm({
          role_name: "",
          description: "",
          is_active: true,
          is_manager: false,
          is_admin: false,
          is_board: false,
          hierarchy_level: null,
        });
        loadData();
      }
    } catch (error) {
      showNotification("Error creating role: " + error.message, "error");
    }
  };

  const handleUpdateRole = async () => {
    try {
      const response = await rolesAPI.updateRole(editingRole.id, roleForm);
      if (response.success) {
        showNotification("Role updated successfully");
        setRoleDialogOpen(false);
        setEditingRole(null);
        setRoleForm({ role_name: "", description: "", is_active: true });
        loadData();
      }
    } catch (error) {
      showNotification("Error updating role: " + error.message, "error");
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (window.confirm("Are you sure you want to delete this role?")) {
      try {
        const response = await rolesAPI.deleteRole(roleId);
        if (response.success) {
          showNotification("Role deleted successfully");
          loadData();
        }
      } catch (error) {
        showNotification("Error deleting role: " + error.message, "error");
      }
    }
  };

  // Permission management
  const handleManagePermissions = async (role) => {
    setSelectedRoleForPermissions(role);
    try {
      // Check if role already has permissions in the roles data
      const roleWithPermissions = roles.find((r) => r.id === role.id);
      let rolePermissionIds = [];

      if (
        roleWithPermissions &&
        roleWithPermissions.permissions &&
        Array.isArray(roleWithPermissions.permissions)
      ) {
        rolePermissionIds = roleWithPermissions.permissions.map((p) => p.id);
      }

      // Also try to get from menu permissions API
      try {
        const response = await rolesAPI.getRoleMenuPermissions(role.id);

        if (response.success && response.data) {
          if (
            response.data.permissions &&
            Array.isArray(response.data.permissions)
          ) {
            const apiPermissionIds = response.data.permissions.map((p) => p.id);
            // Merge both sources of permissions
            rolePermissionIds = [
              ...new Set([...rolePermissionIds, ...apiPermissionIds]),
            ];
          }
        }
      } catch (apiError) {
        console.log(
          "API call failed, using role data permissions only:",
          apiError
        );
      }

      setSelectedPermissions(rolePermissionIds);
      setPermissionDialogOpen(true);
    } catch (error) {
      console.error("Error loading role permissions:", error);
      // Still open the dialog with empty permissions
      setSelectedPermissions([]);
      setPermissionDialogOpen(true);
      showNotification("Could not load existing permissions", "warning");
    }
  };

  const handleUpdatePermissions = async (permissionIds) => {
    try {
      // Ensure permission IDs are integers
      const validPermissionIds = permissionIds
        .map((id) => parseInt(id, 10))
        .filter((id) => !isNaN(id));

      console.log("🔍 Frontend: Sending permission update:");
      console.log("Selected role:", selectedRoleForPermissions);
      console.log("Permission IDs:", validPermissionIds);

      const response = await permissionsAPI.updateRolePermissions(
        selectedRoleForPermissions.id,
        { permission_ids: validPermissionIds }
      );
      if (response.success) {
        showNotification("Permissions updated successfully");
        setPermissionDialogOpen(false);
        setSelectedRoleForPermissions(null);
        setSelectedPermissions([]);
        // Reload roles data to reflect the updated permissions
        await loadData();
      }
    } catch (error) {
      showNotification("Error updating permissions: " + error.message, "error");
    }
  };

  const handlePermissionsChange = (permissionIds) => {
    setSelectedPermissions(permissionIds);
  };

  // UI event handlers
  const handleMenuOpen = (event, role) => {
    setAnchorEl(event.currentTarget);
    setSelectedRole(role);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRole(null);
  };

  const handleEditRole = (role) => {
    setEditingRole(role);
    setRoleForm({
      role_name: role.role_name,
      description: role.description || "",
      is_active: role.is_active !== false,
      is_manager: role.is_manager || false,
      is_admin: role.is_admin || false,
      is_board: role.is_board || false,
      hierarchy_level: role.hierarchy_level || null,
    });
    setRoleDialogOpen(true);
    handleMenuClose();
  };

  const handleAddNewRole = () => {
    setEditingRole(null);
    setRoleForm({
      role_name: "",
      description: "",
      is_active: true,
      is_manager: false,
      is_admin: false,
      is_board: false,
      hierarchy_level: null,
    });
    setRoleDialogOpen(true);
  };

  // Filter roles based on search term - ensure roles is an array
  const filteredRoles = (roles || []).filter(
    (role) =>
      role.role_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      role.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group permissions by module - ensure permissions is an array
  const groupedPermissions = (permissions || []).reduce((acc, permission) => {
    const module = permission.module || "General";
    if (!acc[module]) {
      acc[module] = [];
    }
    acc[module].push(permission);
    return acc;
  }, {});

  const getModuleIcon = (module) => {
    const icons = {
      Dashboard: <DashboardIcon />,
      Employees: <PeopleIcon />,
      Departments: <BusinessIcon />,
      Finance: <FinanceIcon />,
      Procurement: <ProcurementIcon />,
      Leave: <LeaveIcon />,
      Reports: <ReportsIcon />,
      Settings: <SettingsIcon />,
    };
    return icons[module] || <SecurityIcon />;
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box mb={3}>
        <Typography variant="h4" gutterBottom>
          Role Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage system roles and their permissions
        </Typography>
      </Box>

      {/* Action Bar */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
          >
            <TextField
              placeholder="Search roles..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 300 }}
            />
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadData}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddNewRole}
              >
                Add New Role
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Roles Grid */}
      <Grid container spacing={3}>
        {filteredRoles.map((role) => (
          <Grid item xs={12} sm={6} md={4} key={role.id}>
            <Card sx={{ height: "100%" }}>
              <CardHeader
                avatar={
                  <Avatar
                    sx={{
                      bgcolor: role.is_default
                        ? "primary.main"
                        : "secondary.main",
                    }}
                  >
                    <GroupIcon />
                  </Avatar>
                }
                action={
                  <IconButton onClick={(e) => handleMenuOpen(e, role)}>
                    <MoreVertIcon />
                  </IconButton>
                }
                title={role.role_name}
                subheader={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      size="small"
                      label={role.is_default ? "System" : "Custom"}
                      color={role.is_default ? "primary" : "default"}
                    />
                    {role.user_count && (
                      <Chip
                        size="small"
                        label={`${role.user_count} users`}
                        variant="outlined"
                      />
                    )}
                  </Stack>
                }
              />
              <CardContent>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {role.description || "No description provided"}
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="caption" color="text.secondary">
                    Created:{" "}
                    {role.created_at
                      ? new Date(role.created_at).toLocaleDateString()
                      : "N/A"}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<PermissionIcon />}
                    onClick={() => handleManagePermissions(role)}
                  >
                    Permissions
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {filteredRoles.length === 0 && (
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="text.secondary">
            No roles found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {searchTerm
              ? "Try adjusting your search criteria"
              : "Create your first role to get started"}
          </Typography>
          {!searchTerm && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddNewRole}
            >
              Add New Role
            </Button>
          )}
        </Box>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleEditRole(selectedRole)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Role</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleManagePermissions(selectedRole)}>
          <ListItemIcon>
            <PermissionIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Manage Permissions</ListItemText>
        </MenuItem>
        {selectedRole && !selectedRole.is_default && (
          <MenuItem
            onClick={() => handleDeleteRole(selectedRole.id)}
            sx={{ color: "error.main" }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete Role</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Create/Edit Role Dialog */}
      <Dialog
        open={roleDialogOpen}
        onClose={() => setRoleDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingRole ? "Edit Role" : "Create New Role"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Role Name"
              value={roleForm.role_name}
              onChange={(e) =>
                setRoleForm({ ...roleForm, role_name: e.target.value })
              }
              sx={{ mb: 2 }}
              required
            />
            <TextField
              fullWidth
              label="Description"
              value={roleForm.description}
              onChange={(e) =>
                setRoleForm({ ...roleForm, description: e.target.value })
              }
              multiline
              rows={3}
              sx={{ mb: 2 }}
            />

            <Divider sx={{ my: 2 }} />

            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 2 }}
            >
              Role Hierarchy (Select role type and level)
            </Typography>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={roleForm.is_manager}
                      onChange={(e) =>
                        setRoleForm({
                          ...roleForm,
                          is_manager: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Manager"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={roleForm.is_admin}
                      onChange={(e) =>
                        setRoleForm({ ...roleForm, is_admin: e.target.checked })
                      }
                    />
                  }
                  label="Admin/Executive"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={roleForm.is_board}
                      onChange={(e) =>
                        setRoleForm({ ...roleForm, is_board: e.target.checked })
                      }
                    />
                  }
                  label="Board Member"
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              label="Hierarchy Level (optional)"
              type="number"
              value={roleForm.hierarchy_level || ""}
              onChange={(e) =>
                setRoleForm({
                  ...roleForm,
                  hierarchy_level: e.target.value
                    ? parseInt(e.target.value)
                    : null,
                })
              }
              helperText="Higher number = higher authority (e.g., 100 for Admin, 80 for Manager, 50 for Staff)"
              sx={{ mb: 2 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={roleForm.is_active}
                  onChange={(e) =>
                    setRoleForm({ ...roleForm, is_active: e.target.checked })
                  }
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={editingRole ? handleUpdateRole : handleCreateRole}
            disabled={!roleForm.role_name.trim()}
          >
            {editingRole ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permissions Dialog - New Tree View */}
      <Dialog
        open={permissionDialogOpen}
        onClose={() => {
          setPermissionDialogOpen(false);
          setSelectedRoleForPermissions(null);
          setSelectedPermissions([]);
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: "90vh", maxHeight: "900px" },
        }}
      >
        <DialogTitle>
          <Box>
            <Typography variant="h5" component="div" color="primary" gutterBottom>
              Manage Permissions
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Role: <strong>{selectedRoleForPermissions?.role_name}</strong>
            </Typography>
            {selectedRoleForPermissions?.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {selectedRoleForPermissions.description}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3, height: "calc(100% - 140px)", overflow: 'auto' }}>
          {selectedRoleForPermissions && (
            <PermissionTreeView
              roleId={selectedRoleForPermissions.id}
              onPermissionsChange={handlePermissionsChange}
              initialPermissions={selectedPermissions}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
          <Button
            onClick={() => {
              setPermissionDialogOpen(false);
              setSelectedRoleForPermissions(null);
              setSelectedPermissions([]);
            }}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => handleUpdatePermissions(selectedPermissions)}
            startIcon={<SaveIcon />}
            disabled={!selectedRoleForPermissions}
          >
            Save Permissions
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default RoleManagementPage;
